import React from 'react';
import { ElementGroupNames } from '../../../../common/src/enums/ElementGroupNames';
import {
    DataPanelCell,
    DataPanelRow,
    EditPanelCell,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelResultHeader,
    PanelWithoutForm,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableRow,
    TextWithLineBreaks,
} from '../../components/elements';
import { AssetTemplateList } from '../../components/Surveys/Common/AssetTemplateList';
import { NullableFormattedDate } from '../../components/utils/NullableFormattedDate';
import { NullableFormattedNumber } from '../../components/utils/NullableFormattedNumber';
import { useReactAsync } from '../../components/utils/useReactAsync';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchGet } from '../../store/openApiFetch';
import { SelectedOptionToText } from './SelectedOptionToText';
import { SelectedOptionToTextWithOther } from './SelectedOptionToTextWithOther';
import {
    BreakDownOpenState,
    SurveyDetailsElementGroupPanel,
    SurveyDetailsElementGroupPanelDetails,
    SurveyDetailsElementGroupReportPanel,
    SurveyDetailsElementPanel,
    SurveyDetailsUnitPanel,
} from './surveyDetailsElements';
import { VerificatieBerekeningScoreLabel } from './VerificatieBerekeningScoreLabel';
import { verificatieBerekeningSelectOptions } from './verificatieBerekeningSelectOptions';

async function loadElements({ surveyId }: { surveyId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/elements/verificatieBerekening', { surveyId });
    return result;
}

export function SurveyDetailsVerificatieBerekeningReport(props: { surveyId: string }) {
    const translate = useTranslate();
    const { data } = useReactAsync(loadElements, { surveyId: props.surveyId }, []);
    const [breakDownOpenState, setBreakDownOpenState] = React.useState<BreakDownOpenState>({});

    function expandAll() {
        if (!data) {
            return;
        }
        const expandedBreakDownOpenState = {} as { [key: string]: boolean };
        expandedBreakDownOpenState[ElementGroupNames.Superstructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Substructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Foundation] = true;
        for (const elementGroup of data) {
            expandedBreakDownOpenState[elementGroup.elementGroupName] = true;
            for (const element of elementGroup.elements) {
                expandedBreakDownOpenState[element.id] = true;
                for (const unit of element.units) {
                    expandedBreakDownOpenState[unit.id] = true;
                    for (const manifestation of unit.manifestations) {
                        expandedBreakDownOpenState[manifestation.id] = true;
                    }
                }
            }
        }
        setBreakDownOpenState(expandedBreakDownOpenState);
    }
    function collapseAll() {
        setBreakDownOpenState({});
    }

    function onChangeBreakdownOpenState(key: string) {
        breakDownOpenState[key] = !breakDownOpenState[key];
        setBreakDownOpenState({ ...breakDownOpenState });
    }

    if (!data) {
        return null;
    }

    return (
        <PanelWithoutForm id="VerificatieBerekeningRapport">
            <PanelHeading text="Rapport" icon="fa fa-tasks"></PanelHeading>
            <PanelBody whiteBackground={true}>
                <PanelButtonLink
                    onClick={() => expandAll()}
                    text={translate('survey.expandAllComposition')}
                    icon="fa fa-plus-square-o"
                    xsmall={true}
                    rightAllign={true}
                />
                <PanelButtonLink
                    onClick={() => collapseAll()}
                    text={translate('survey.collapseAllComposition')}
                    icon="fa fa-minus-square-o "
                    xsmall={true}
                    rightAllign={true}
                />
                <SurveyDetailsElementGroupReportPanel extraColumns={['Toetsing']}>
                    {data.map((elementGroup) => (
                        <SurveyDetailsElementGroupPanel
                            elementGroupName={elementGroup.elementGroupName}
                            key={elementGroup.elementGroupName}
                            hasChildren={elementGroup.inspectionStandardData !== undefined || elementGroup.elements.length > 0}
                            breakDownOpenState={breakDownOpenState}
                            onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                            extraColumns={[
                                <>
                                    {elementGroup.inspectionStandardData && (
                                        <>
                                            <VerificatieBerekeningScoreLabel score={elementGroup.inspectionStandardData.score} />{' '}
                                        </>
                                    )}
                                </>,
                            ]}
                        >
                            <SurveyDetailsElementGroupPanelDetails>
                                <DataPanelRow>
                                    <DataPanelCell rowCount={4} text="Project">
                                        {elementGroup.inspectionStandardData?.projectNaam}{' '}
                                    </DataPanelCell>
                                    <DataPanelCell rowCount={4} text="Datum">
                                        <NullableFormattedDate
                                            value={elementGroup.inspectionStandardData?.berekendOpDatum}
                                        ></NullableFormattedDate>{' '}
                                    </DataPanelCell>
                                    <DataPanelCell rowCount={4} text="Status">
                                        <SelectedOptionToText
                                            selectedValue={elementGroup.inspectionStandardData?.status}
                                            options={verificatieBerekeningSelectOptions.status}
                                        />
                                    </DataPanelCell>
                                </DataPanelRow>
                                <DataPanelRow>
                                    <DataPanelCell rowCount={12} text="Constructeur">
                                        {elementGroup.inspectionStandardData?.constructeur}{' '}
                                    </DataPanelCell>
                                </DataPanelRow>
                                <PanelResultHeader>Uitgangspunten</PanelResultHeader>
                                <DataPanelRow>
                                    <DataPanelCell rowCount={6} text="Toets niveau">
                                        <SelectedOptionToText
                                            selectedValue={elementGroup.inspectionStandardData?.toetsNiveau}
                                            options={verificatieBerekeningSelectOptions.toetsNiveau}
                                        />
                                    </DataPanelCell>
                                    <DataPanelCell rowCount={6} text="Norm versie">
                                        <SelectedOptionToTextWithOther
                                            selectedValue={elementGroup.inspectionStandardData?.normVersie}
                                            options={verificatieBerekeningSelectOptions.normVersie}
                                            otherId="7"
                                            otherValue={elementGroup.inspectionStandardData?.normVersieAnders}
                                        />
                                    </DataPanelCell>
                                </DataPanelRow>
                                <DataPanelRow>
                                    <DataPanelCell rowCount={6} text="Modelleer niveau">
                                        <SelectedOptionToText
                                            selectedValue={elementGroup.inspectionStandardData?.modelleerNiveau}
                                            options={verificatieBerekeningSelectOptions.modelleerNiveau}
                                        />
                                    </DataPanelCell>
                                    <DataPanelCell rowCount={6} text="Gebruikte restlevensduur">
                                        {elementGroup.inspectionStandardData?.restLevensduur}{' '}
                                    </DataPanelCell>
                                </DataPanelRow>
                                <DataPanelRow>
                                    <DataPanelCell rowCount={6} text="Wegindeling">
                                        <SelectedOptionToText
                                            selectedValue={elementGroup.inspectionStandardData?.wegindeling}
                                            options={verificatieBerekeningSelectOptions.wegindeling}
                                        />
                                    </DataPanelCell>
                                    <DataPanelCell rowCount={6} text="Zware voertuigen per jaar">
                                        <SelectedOptionToTextWithOther
                                            selectedValue={elementGroup.inspectionStandardData?.aantalZwareVoertuigenPerJaar}
                                            options={verificatieBerekeningSelectOptions.aantalZwareVoertuigenPerJaar}
                                            otherId="9"
                                            otherValue={elementGroup.inspectionStandardData?.aantalZwareVoertuigenPerJaarAnders}
                                        />
                                    </DataPanelCell>
                                </DataPanelRow>
                                <DataPanelRow>
                                    <DataPanelCell rowCount={6} text="Beperking voertuig gewicht">
                                        <SelectedOptionToText
                                            selectedValue={elementGroup.inspectionStandardData?.beperkingVoertuigGewicht}
                                            options={verificatieBerekeningSelectOptions.beperkingVoertuigGewicht}
                                        />
                                    </DataPanelCell>
                                    <DataPanelCell rowCount={6} text="Beperking aslast">
                                        <SelectedOptionToText
                                            selectedValue={elementGroup.inspectionStandardData?.beperkingAslast}
                                            options={verificatieBerekeningSelectOptions.beperkingAslast}
                                        />
                                    </DataPanelCell>
                                </DataPanelRow>
                                <PanelResultHeader>Opmerking</PanelResultHeader>
                                <DataPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <TextWithLineBreaks text={elementGroup.inspectionStandardData?.opmerking} />
                                    </EditPanelCell>
                                </DataPanelRow>
                                <PanelResultHeader>Documenten</PanelResultHeader>
                                <DataPanelRow>
                                    <AssetTemplateList assets={elementGroup.inspectionStandardData?.bestanden} />
                                </DataPanelRow>
                            </SurveyDetailsElementGroupPanelDetails>
                            {elementGroup.elements.map((element) => (
                                <SurveyDetailsElementPanel
                                    extraColumns={[<VerificatieBerekeningScoreLabel key="key" score={element.score} />]}
                                    key={element.id}
                                    breakDownOpenState={breakDownOpenState}
                                    onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                                    element={element}
                                >
                                    {element.units.map((unit) => (
                                        <SurveyDetailsUnitPanel
                                            extraColumns={[<VerificatieBerekeningScoreLabel key="key" score={unit.inspectionStandardData?.score} />]}
                                            key={unit.id}
                                            breakDownOpenState={breakDownOpenState}
                                            onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                                            unit={unit}
                                        >
                                            {unit.inspectionStandardData && (
                                                <>
                                                    <Table sm>
                                                        <TableHead>
                                                            <TableHeadRow>
                                                                <TableData>Type</TableData>
                                                                <TableData>Modelleer niveau</TableData>
                                                                <TableData>Unity check</TableData>
                                                                <TableData />
                                                            </TableHeadRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {unit.inspectionStandardData.unityChecks.map((unityCheck) => (
                                                                <TableRow key={unityCheck.id}>
                                                                    <TableData>
                                                                        <SelectedOptionToTextWithOther
                                                                            selectedValue={unityCheck.unityCheckType}
                                                                            options={verificatieBerekeningSelectOptions.unityCheckType}
                                                                            otherId="23"
                                                                            otherValue={unityCheck.unityCheckTypeAnders}
                                                                        />{' '}
                                                                    </TableData>
                                                                    <TableData>
                                                                        <SelectedOptionToText
                                                                            selectedValue={unityCheck.modelleerNiveau}
                                                                            options={verificatieBerekeningSelectOptions.modelleerNiveau}
                                                                        />
                                                                    </TableData>
                                                                    <TableData>
                                                                        <NullableFormattedNumber value={unityCheck.unityCheckScore} />
                                                                    </TableData>
                                                                    <TableData>
                                                                        <VerificatieBerekeningScoreLabel score={unityCheck.score} />
                                                                    </TableData>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                    <PanelResultHeader>Opmerking</PanelResultHeader>
                                                    <TextWithLineBreaks text={unit.inspectionStandardData.opmerking} />
                                                </>
                                            )}
                                        </SurveyDetailsUnitPanel>
                                    ))}
                                </SurveyDetailsElementPanel>
                            ))}
                        </SurveyDetailsElementGroupPanel>
                    ))}
                </SurveyDetailsElementGroupReportPanel>
            </PanelBody>
        </PanelWithoutForm>
    );
}
